import { useRef, useState } from "react";
import type { StickerInstance } from "@/types";

export function StickerInstanceCard({
  instance,
  scaleRef,
  selected,
  onSelect,
  onMove,
  onSavePosition,
  onResize,
  onSaveSize,
}: {
  instance: StickerInstance;
  scaleRef: React.RefObject<number>;
  selected: boolean;
  onSelect: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onSavePosition: (id: string, x: number, y: number) => void;
  onResize: (id: string, w: number, h: number) => void;
  onSaveSize: (id: string, w: number, h: number) => void;
}) {
  const [lifted, setLifted] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragging = useRef(false);
  const resizingRef = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });
  const instanceRef = useRef(instance);
  instanceRef.current = instance;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onSaveRef = useRef(onSavePosition);
  onSaveRef.current = onSavePosition;
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const onSaveSizeRef = useRef(onSaveSize);
  onSaveSizeRef.current = onSaveSize;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(instance._id);
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    setLifted(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - lastMouse.current.x) / s;
    const dy = (e.clientY - lastMouse.current.y) / s;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    const inst = instanceRef.current;
    onMoveRef.current(inst._id, inst.x + dx, inst.y + dy);
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setLifted(false);
    const inst = instanceRef.current;
    onSaveRef.current(inst._id, inst.x, inst.y);
  };

  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(instance._id);
    resizingRef.current = true;
    resizeStart.current = { mx: e.clientX, my: e.clientY, w: instance.w, h: instance.h };
    e.currentTarget.setPointerCapture(e.pointerId);
    setResizing(true);
  };

  const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - resizeStart.current.mx) / s;
    const dy = (e.clientY - resizeStart.current.my) / s;
    const newW = Math.max(40, resizeStart.current.w + dx);
    const newH = Math.max(40, resizeStart.current.h + dy);
    onResizeRef.current(instanceRef.current._id, newW, newH);
  };

  const onResizePointerUp = () => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    setResizing(false);
    const inst = instanceRef.current;
    onSaveSizeRef.current(inst._id, inst.w, inst.h);
  };

  const showHandle = selected || hovered || resizing;

  return (
    <div
      style={{
        position: "absolute",
        left: instance.x,
        top: instance.y,
        width: instance.w,
        height: instance.h,
        zIndex: lifted || resizing ? 12 : 6,
        pointerEvents: "auto",
        cursor: lifted ? "grabbing" : "grab",
        filter: lifted
          ? "drop-shadow(0 12px 28px rgba(0,0,0,0.28))"
          : "drop-shadow(0 2px 8px rgba(0,0,0,0.12))",
        transform: lifted ? "scale(1.05)" : "scale(1)",
        transition: lifted
          ? "transform 0.08s ease-out, filter 0.08s ease-out"
          : "transform 0.18s ease-out, filter 0.18s ease-out",
        outline: selected ? "2px solid #0091ff" : "2px solid transparent",
        outlineOffset: 2,
        borderRadius: 4,
        touchAction: "none",
        userSelect: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseDown={(e) => { e.stopPropagation(); onSelect(instance._id); }}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={`/api/sticker-images/${instance.stickerId}`}
        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", pointerEvents: "none" }}
        draggable={false}
        alt=""
      />
      {/* Resize handle */}
      <div
        style={{
          position: "absolute",
          bottom: -5,
          right: -5,
          width: 10,
          height: 10,
          borderRadius: 2,
          background: selected ? "#0091ff" : "rgba(0,0,0,0.4)",
          cursor: "nwse-resize",
          touchAction: "none",
          opacity: showHandle ? 1 : 0,
          transition: "opacity 0.15s",
          border: "1.5px solid white",
        }}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
      />
    </div>
  );
}
